import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Flex, Box, Heading, Text, Card, CardHeader, CardContent, Button, TextField } from '@chatsdet/ui';
import { API_URL } from '../../../config';

type DedupPreviewResponse = {
  ok: boolean;
  decision?: {
    kind: 'existing' | 'new';
    issueId?: string;
    reasoning: string;
  };
  error?: string;
};

export function SuperadminDedup() {
  const [dedupIssueId, setDedupIssueId] = React.useState('');

  const dedupMutation = useMutation<{ decision: DedupPreviewResponse['decision'] }, Error, string>({
    mutationFn: async (issueId: string) => {
      const response = await fetch(`${API_URL}/superadmin/issues/dedup-preview`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ issueId }),
      });

      const body: DedupPreviewResponse = await response.json();
      if (!response.ok || !body.ok || !body.decision) {
        throw new Error(body.error ?? 'Failed to run dedup preview');
      }
      return { decision: body.decision };
    },
  });

  const trimmedDedupIssueId = dedupIssueId.trim();

  return (
    <Card padding="lg">
      <CardHeader>
        <Heading level={3}>Dedup Inspector</Heading>
        <Text size="sm" className="text-muted-foreground mt-1">
          Manually invoke the dedup agent for a specific issue to inspect its reasoning.
        </Text>
      </CardHeader>
      <CardContent>
        <Flex direction="column" gap="md">
          <TextField
            label="Issue ID"
            placeholder="00000000-0000-0000-0000-000000000000"
            value={dedupIssueId}
            onChange={(value) => setDedupIssueId(String(value))}
            isDisabled={dedupMutation.isPending}
          />
          <Flex gap="sm" align="center">
            <Button
              onPress={() => {
                if (!trimmedDedupIssueId) return;
                dedupMutation.mutateAsync(trimmedDedupIssueId);
              }}
              isDisabled={!trimmedDedupIssueId || dedupMutation.isPending}
            >
              {dedupMutation.isPending ? 'Running…' : 'Run Dedup Agent'}
            </Button>
            {dedupMutation.isError ? (
              <Text size="sm" className="text-destructive">
                {dedupMutation.error?.message ?? 'Failed to run dedup preview'}
              </Text>
            ) : null}
          </Flex>
          {dedupMutation.data?.decision ? (
            <Box className="border border-border rounded-md p-4 space-y-2 bg-muted/40">
              <Heading level={4}>Result</Heading>
              <Text>
                Classification:{' '}
                {dedupMutation.data.decision.kind === 'existing'
                  ? 'Existing issue'
                  : 'New issue'}
              </Text>
              {dedupMutation.data.decision.issueId ? (
                <Text>Matched issue ID: {dedupMutation.data.decision.issueId}</Text>
              ) : null}
              <Text size="sm" className="text-muted-foreground whitespace-pre-line">
                Reasoning: {dedupMutation.data.decision.reasoning}
              </Text>
            </Box>
          ) : null}
        </Flex>
      </CardContent>
    </Card>
  );
}
